export type MapPropKind = 'wrecked-tank' | 'aircraft-wreck' | 'barrels' | 'crates' | 'debris' | 'turret-wreck' | 'weapon-cache' | 'sensor-array' | 'symbiosis-pod' | 'command-crate' | 'boulders' | 'alien-tree' | 'rocky-patch' | 'acid-pool';

export type MapPropHeight = 'low' | 'medium' | 'high';

/** Decorative authoring objects shared by Map Forge and live battle scenes. */
export interface MapPropDef {
  id: string;
  kind: MapPropKind;
  x: number;
  y: number;
  rotation: number;
  scale: number;
  depth: number;
  height: MapPropHeight;
  /** Reserved for the line-of-sight pass; decorative props default to false. */
  blocksLineOfSight: boolean;
  /** Optional atlas frame for generated environmental prop sheets. */
  frame?: number;
}

export const MAP_PROP_KINDS: MapPropKind[] = ['wrecked-tank', 'aircraft-wreck', 'barrels', 'crates', 'debris', 'turret-wreck', 'weapon-cache', 'sensor-array', 'symbiosis-pod', 'command-crate', 'boulders', 'alien-tree', 'rocky-patch', 'acid-pool'];

export const MAP_PROP_LABELS: Record<MapPropKind, string> = {
  'wrecked-tank': 'WRECKED TANK',
  'aircraft-wreck': 'AIRCRAFT',
  barrels: 'BARRELS',
  crates: 'CRATES',
  debris: 'DEBRIS',
  'turret-wreck': 'TURRET WRECK',
  'weapon-cache': 'WEAPON CACHE',
  'sensor-array': 'SENSOR ARRAY',
  'symbiosis-pod': 'SYMBIOSIS POD',
  'command-crate': 'COMMAND CRATE',
  boulders: 'BOULDERS',
  'alien-tree': 'ALIEN TREE',
  'rocky-patch': 'ROCKY PATCH',
  'acid-pool': 'ACID POOL',
};

export const MAP_PROP_TEXTURES: Record<MapPropKind, string> = {
  'wrecked-tank': 'map-prop-wrecked-tank',
  'aircraft-wreck': 'map-prop-aircraft',
  barrels: 'map-prop-barrels',
  crates: 'map-prop-crates',
  debris: 'map-prop-debris',
  'turret-wreck': 'map-prop-turret-wreck',
  'weapon-cache': 'map-prop-weapon-cache',
  'sensor-array': 'map-prop-sensor-array',
  'symbiosis-pod': 'map-prop-symbiosis-pod',
  'command-crate': 'map-prop-command-crate',
  boulders: 'map-prop-environmental-life',
  'alien-tree': 'map-prop-environmental-life',
  'rocky-patch': 'map-prop-environmental-life',
  'acid-pool': 'map-prop-environmental-life',
};

export const MAP_PROP_FRAMES: Partial<Record<MapPropKind, number>> = {
  boulders: 0,
  'alien-tree': 1,
  'rocky-patch': 2,
  'acid-pool': 3,
};

export const MAP_PROP_HEIGHTS: Record<MapPropKind, MapPropHeight> = {
  'wrecked-tank': 'high',
  'aircraft-wreck': 'high',
  barrels: 'medium',
  crates: 'medium',
  debris: 'low',
  'turret-wreck': 'high',
  'weapon-cache': 'medium',
  'sensor-array': 'medium',
  'symbiosis-pod': 'low',
  'command-crate': 'low',
  boulders: 'medium',
  'alien-tree': 'high',
  'rocky-patch': 'low',
  'acid-pool': 'low',
};

export const MAP_PROP_DEFAULT_SCALES: Record<MapPropKind, number> = {
  'wrecked-tank': 0.16,
  'aircraft-wreck': 0.16,
  barrels: 0.12,
  crates: 0.12,
  debris: 0.12,
  'turret-wreck': 0.18,
  'weapon-cache': 0.1,
  'sensor-array': 0.1,
  'symbiosis-pod': 0.1,
  'command-crate': 0.08,
  boulders: 0.16,
  'alien-tree': 0.14,
  'rocky-patch': 0.16,
  'acid-pool': 0.14,
};

export function makeMapProp(kind: MapPropKind, x: number, y: number, index: number): MapPropDef {
  return {
    id: `prop-${Date.now()}-${index}-${Math.random().toString(36).slice(2, 6)}`,
    kind,
    x,
    y,
    rotation: 0,
    scale: MAP_PROP_DEFAULT_SCALES[kind],
    depth: 1,
    height: MAP_PROP_HEIGHTS[kind],
    blocksLineOfSight: false,
    frame: MAP_PROP_FRAMES[kind],
  };
}
